import React, { useState } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Select from 'react-select';
import './SalesPopup.css';
import { useNavigate } from 'react-router-dom';

const SalesPopup = ({ isOpen, onClose, onSubmit }) => {
  const salesPerson = window.localStorage.getItem('projectUserUID');
  const salesPersonName = window.localStorage.getItem('projectUserDisplayName');
  const [projectId, setProjectId] = useState('');
   const navigate = useNavigate();
  
  // Payment mode options for React-select
  const paymentModeOptions = [
    { value: 'CreditCard', label: 'Credit Card' },
    { value: 'DebitCard', label: 'Debit Card' },
    { value: 'Cash', label: 'Cash' },
    { value: 'OnlineLink', label: 'Online Link' },
    { value: 'UPI', label: 'UPI' },
    { value: 'BankTransfer', label: 'Bank Transfer' },
    { value: 'Cheque', label: 'Cheque' }
  ];

  // Custom styles for React-select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '40px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      borderColor: state.isFocused ? '#3b82f6' : '#ccc',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
      '&:hover': {
        borderColor: '#3b82f6'
      }
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#3b82f6',
      borderRadius: '4px'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'white',
      fontSize: '14px'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'white',
      '&:hover': {
        backgroundColor: '#2563eb',
        color: 'white'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
      fontSize: '14px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#3b82f6' 
        : state.isFocused 
          ? '#eff6ff' 
          : 'white',
      color: state.isSelected ? 'white' : '#374151',
      padding: '8px 12px',
      fontSize: '14px'
    })
  };
  
  // Generate project ID from backend when the popup opens
  React.useEffect(() => {
    if (isOpen && !projectId) {
      generateProjectId();
    }
  }, [isOpen]);

  const generateProjectId = async () => {
    try {
      const response = await fetch('https://api-4h4srbodqa-uc.a.run.app/api/generate-project-id', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate project ID');
      }
      
      const result = await response.json();
      setProjectId(result.projectId);
    } catch (error) {
      console.error('Error generating project ID:', error);
      setError('Failed to generate project ID. Please try again.');
    }
  };
  
  const [formData, setFormData] = useState({
    name: '',
    clientName: '',
    mobNumber: '',
    cxEmail: '',
    description: '',
    salesPersonId: salesPerson,
    salesPersonName: salesPersonName,
    bookingAmount: '',
    closureDate: '',
    designStartDate:'',
    discount: '',
    meetingDate: '', //Meeting Date
    propertyName: '',
    address: '',
    city: '',
    source: '',
    txnId: '',
    projectValue: 0,
    gst: 0,
    increment: 0,
    paymentModes: [] // Changed to array for multiple selection
  });
  
  // Updated files state to handle multiple files for all inputs
  const [files, setFiles] = useState({
    salesBOQ: [], // Changed to array
    // DiscWordDoc: [], // Changed to array
    floorPlan: [], // Changed to array
    txnImages: [] // Already array
  });
  
  // Updated file URLs to handle multiple files for all inputs
  const [fileUrls, setFileUrls] = useState({
    salesBOQFile: [], // Changed to array
    // DiscWordDoc: [], // Changed to array
    floorPlan: [], // Changed to array
    txnImages: [] // Already array
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({
    salesBOQ: 0,
    // DiscWordDoc: 0,
    floorPlan: 0,
    txnImages: 0
  });

  const handleChange = (e) => {
    if (e.target.type === 'file') {
      // Handle multiple file selection for all file inputs
      const selectedFiles = Array.from(e.target.files);
      
      // Validate each file
      for (const file of selectedFiles) {
        if (file.size > 5 * 1024 * 1024) {
          setError(`${file.name} is too large. Maximum file size is 5MB.`);
          return;
        }

        const allowedTypes = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        if (!allowedTypes.includes(fileExtension)) {
          setError(`Invalid file type for ${file.name}. Allowed types are: ${allowedTypes.join(', ')}`);
          return;
        }
      }

      // Update files state for all file inputs
      setFiles(prev => ({
        ...prev,
        [e.target.name]: selectedFiles
      }));
      
      // Clear error if files are valid
      setError(null);
    } else {
      setFormData(prev => ({
        ...prev,
        [e.target.name]: e.target.value
      }));
    }
  };

  // Handle React-select payment mode selection
  const handlePaymentModeChange = (selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setFormData(prev => ({
      ...prev,
      paymentModes: selectedValues
    }));
  };

  const uploadToFirebase = async (file, fileName, index = null) => {
    if (!file) return null;

    try {
      const storage = getStorage();
      const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
      // Use the generated projectId for the storage path
      const storageRef = index !== null 
        ? ref(storage, `${projectId}/${fileName}/${fileName}_${index}${fileExtension}`)
        : ref(storage, `${projectId}/${fileName}/${fileName}${fileExtension}`);

      // Upload file
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadUrl = await getDownloadURL(snapshot.ref);
      return downloadUrl;
    } catch (error) {
      console.error(`Error uploading ${fileName}:`, error);
      throw new Error(`Error uploading ${fileName}: ${error.message}`);
    }
  };

  // Generic function to upload multiple files
  const uploadMultipleFiles = async (fileArray, folderName) => {
    if (!fileArray || fileArray.length === 0) return [];

    const uploadPromises = fileArray.map((file, index) => 
      uploadToFirebase(file, folderName, index)
    );

    return await Promise.all(uploadPromises);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Upload all files to Firebase Storage
      const [salesBOQUrls, floorPlanUrls, txnImagesUrls] = await Promise.all([
         files.txnImages.length > 0 ? uploadMultipleFiles(files.txnImages, 'finance-docs') : Promise.resolve([]),
        files.salesBOQ.length > 0 ? uploadMultipleFiles(files.salesBOQ, 'quotes') : Promise.resolve([]),
        // files.DiscWordDoc.length > 0 ? uploadMultipleFiles(files.DiscWordDoc, 'mom') : Promise.resolve([]),
        files.floorPlan.length > 0 ? uploadMultipleFiles(files.floorPlan, 'site-images') : Promise.resolve([])
       
      ]);

      // Update file URLs
      setFileUrls({
        salesBOQFile: salesBOQUrls || [],
        // DiscWordDoc: DiscWordDocUrls || [],
        floorPlan: floorPlanUrls || [],
        txnImages: txnImagesUrls || []
      });

      // Prepare data for backend
      const submissionData = {
        ...formData,
        projectId,  // This is now the generated FABA{count} ID
        salesBOQFile: salesBOQUrls, // Now array of URLs
        // DiscWordDoc: DiscWordDocUrls, // Now array of URLs
        floorPlan: floorPlanUrls, // Now array of URLs
        txnImages: txnImagesUrls, // Array of URLs
        createdAt: new Date().toISOString()
      };

      // Send data to backend
      const response = await fetch('https://api-4h4srbodqa-uc.a.run.app/api/projects', {
      // const response = await fetch('http://localhost:4000/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        throw new Error('Error saving project data');
      }

      const result = await response.json();
      onSubmit(result);
      onClose();
      navigate(`/project/${projectId}`);
      // Reset project ID for next use
      setProjectId('');
    } catch (error) {
      console.error("Error:", error);
      setError(error.message || 'Error submitting form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="popup1-overlay">
      <div className="popup1">
        <div className="icon1">
          <svg viewBox="0 0 24 24">
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
          </svg>
        </div>
        <h2 style={{color:'#000'}}>Enter Information</h2>
        {projectId && (
          <div className="project-id-display" style={{color: '#666', margin: '10px 0', fontSize: '14px'}}>
            Project ID: {projectId}
          </div>
        )}
        {error && (
          <div className="error-message" style={{color: 'red', margin: '10px 0'}}>
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <input className='input1' type="text" name="name" placeholder="Project Name" required onChange={handleChange} />
            <input className='input1' type="text" name="clientName" placeholder="Client Name" required onChange={handleChange} />
            <input className='input1' type="tel" name="mobNumber" placeholder="Mobile Number" required onChange={handleChange} />
          </div>
          <div className="form-row">
            <input className='input1' type="email" name="cxEmail" placeholder="Client Email" required onChange={handleChange} />
            <select className='input1' name="source" required onChange={handleChange}>
              <option value="">Select Source</option>
              <option value="RawData">Raw Data</option>
              <option value="Google">Google</option>
              <option value="Facebook">Facebook</option>
              <option value="Instagram">Instagram</option>
              <option value="WalkIn">Walk-In</option>
              <option value="Direct">Direct</option>
              <option value="ChannelPartner">Channel Partner</option>
              <option value="Reference">Reference</option>
            </select>
            <input 
              className='input1' 
              type="text" 
              name="meetingDate" 
              placeholder="Meeting Date" 
              required 
              onChange={handleChange} 
              onFocus={(e) => e.target.type = "date"} 
              onBlur={(e) => e.target.value === "" ? e.target.type = "text" : null}
            />
          </div>
          <div className="form-row">
            <input className='input1' type="number" name="projectValue" placeholder="Project Value" step="any" required onChange={handleChange} />
            <input className='input1' type="number" name="discount" placeholder="Discount" step="any" required onChange={handleChange} />
            <input className='input1' type="number" name="bookingAmount" placeholder="Booking Amount" step="any" required onChange={handleChange} />
          </div>
          <div className="form-row">
            <input className='input1' type="number" name="gst" placeholder="GST" step="any" required onChange={handleChange} />
            <input className='input1' type="number" name="increment" placeholder="Increment (%)" step="any" required onChange={handleChange} />
            <input 
              className='input1' 
              type="text" 
              name="closureDate" 
              placeholder="Closure Date" 
              required 
              onChange={handleChange} 
              onFocus={(e) => e.target.type = "date"} 
              onBlur={(e) => e.target.value === "" ? e.target.type = "text" : null}
            />
          </div>

          <div className="form-row">
            <input className='input1' type="text" name="txnId" placeholder="TXN ID" required onChange={handleChange} />
            
            {/* React-select for payment modes */}
            <Select
              className="input1"
              name="paymentModes"
              isMulti
              options={paymentModeOptions}
              onChange={handlePaymentModeChange}
              styles={customSelectStyles}
              placeholder="Select payment modes..."
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              isClearable
              isSearchable
              required
            />

            {/* Multiple file upload for transaction images */}
            <div className="file-input-container">
              <label className="file-input-label">
                <input 
                  className='input1' 
                  type="file" 
                  name="txnImages" 
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.docx" 
                  multiple
                  required 
                  onChange={handleChange} 
                />
                <span className="file-input-placeholder">
                  {files.txnImages.length > 0 
                    ? `${files.txnImages.length} file(s) selected` 
                    : "TXN Images (.jpg, .png, .pdf, .doc, .docx)"}
                </span>
              </label>
            </div>
          </div>

          <div className="form-row">
            <input className='input1' type="text" name="propertyName" placeholder="Property Name" required onChange={handleChange} />
            <input className='input1' type="text" name="address" placeholder="Address" required onChange={handleChange} />
            <input className='input1' type="text" name="city" placeholder="City" required onChange={handleChange} />
          </div>
          <div className="form-row">
            <input 
              className='input1' 
              type="text" 
              name="designStartDate" 
              placeholder="Design Start Date" 
              required 
              onChange={handleChange} 
              onFocus={(e) => e.target.type = "date"} 
              onBlur={(e) => e.target.value === "" ? e.target.type = "text" : null}
            />

            {/* Multiple file upload for floor plans */}
            <div className="file-input-container">
              <label className="file-input-label">
                <input 
                  className='input1' 
                  type="file" 
                  name="floorPlan" 
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.docx" 
                  multiple
                  required 
                  onChange={handleChange} 
                />
                <span className="file-input-placeholder">
                  {files.floorPlan.length > 0 
                    ? `${files.floorPlan.length} file(s) selected` 
                    : "Upload Floor Plans (.pdf, .doc, .docx, .jpg, .png)"}
                </span>
              </label>
            </div>

            {/* Multiple file upload for Sales BOQ */}
            <div className="file-input-container">
              <label className="file-input-label">
                <input 
                  className='input1' 
                  type="file" 
                  name="salesBOQ" 
                  accept=".pdf,.doc,.docx" 
                  multiple
                  required 
                  onChange={handleChange} 
                />
                <span className="file-input-placeholder">
                  {files.salesBOQ.length > 0 
                    ? `${files.salesBOQ.length} file(s) selected` 
                    : "Upload Sales BOQ (.pdf, .doc, .docx)"}
                </span>
              </label>
            </div>
          </div>
          
          <div className="form-row">
            {/* Multiple file upload for Discussion Documents */}
            {/* <div className="file-input-container">
              <label className="file-input-label">
                <input 
                  className='input1' 
                  type="file" 
                  name="DiscWordDoc" 
                  accept=".pdf,.doc,.docx" 
                  multiple
                  required 
                  onChange={handleChange} 
                />
                <span className="file-input-placeholder">
                  {files.DiscWordDoc.length > 0 
                    ? `${files.DiscWordDoc.length} file(s) selected` 
                    : "Upload Discussion Docs (.pdf, .doc, .docx)"}
                </span>
              </label>
            </div> */}
            <textarea 
              className='input12' 
              name="description" 
              placeholder="Description" 
              rows={2}
              required 
              onChange={handleChange}
            ></textarea>
          </div>
          
          <button 
            className='button1' 
            type="submit" 
            disabled={loading || !projectId}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </form>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>
    </div>
  );
};

export default SalesPopup;